import { useState } from 'react';
import { CheckCircle2, Users, Handshake, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  {
    icon: Users,
    title: 'Rigorous Human Vetting',
    description: 'Every candidate is evaluated beyond keywords—assessing experience, leadership, communication, and cultural fit before introduction.',
  },
  {
    icon: Handshake,
    title: 'Structured Placement Support',
    description: 'We coordinate introductions, streamline communication, and remain involved through onboarding to support long-term success.',
  },
  {
    icon: Zap,
    title: 'Immediate Contribution',
    description: 'Our candidates bring maturity, reliability, and real-world problem solving—ready to add value from day one.',
  },
];

const whatSetsUsApart = [
  'Rigorous human vetting beyond keyword filters',
  'Thoughtful matching aligned with both skill and culture',
  'Prepared candidates who understand your expectations',
  'Active coordination through onboarding',
];

export default function Employers() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = new FormData(e.target);
    const data = {
      first_name: formData.get('firstName'),
      last_name: formData.get('lastName'),
      email: formData.get('email'),
      company: formData.get('company'),
      phone: formData.get('phone') || null,
      message: formData.get('message'),
    };

    try {
      const response = await fetch(`${API_URL}/api/employer-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ type: 'success', message: result.message });
        e.target.reset();
      } else {
        setSubmitStatus({ type: 'error', message: result.detail || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Unable to send inquiry. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="employers-page">
      {/* Hero Section */}
      <section className="relative bg-slate-800 overflow-hidden pt-40 md:pt-[calc(12rem+10vh)] pb-12 md:pb-16" data-testid="employers-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              <span className="text-purple-200 italic">Hire Proven Professionals</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              Aim Hire connects you with accomplished, pre-vetted talent—supported by a thoughtful 
              process designed for quality, fit, and long-term success.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="employers-benefits">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {benefits.map((benefit, index) => (
              <div 
                key={benefit.title}
                className="card-hover p-6 rounded-xl border border-slate-100 bg-white text-center"
                data-testid={`employer-benefit-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 
                  className="text-lg font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {benefit.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart Section */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="what-sets-apart">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                A Recruiting Partner — Not a Resume Pipeline
              </h2>
              <p className="text-base text-slate-600 mb-6">
                We work closely with employers to identify accomplished professionals who bring 
                experience, maturity, and reliability to the workplace. Every introduction is 
                intentional. Every placement is thoughtfully supported.
              </p>
              
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
                What sets Aim Hire apart:
              </p>
              <ul className="space-y-3 mb-6">
                {whatSetsUsApart.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-slate-600 text-sm italic border-l-4 border-purple-200 pl-4">
                We prioritize long-term contribution over short-term placements.
              </p>
            </div>

            {/* Inquiry Form */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg" data-testid="employer-form">
              <h3 
                className="text-xl font-semibold text-slate-900 mb-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Get Started
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                Fill out the form below to learn more about our candidates and placement process.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="company" className="text-sm">Company Name</Label>
                    <Input 
                      id="company"
                      name="company"
                      placeholder="Your company" 
                      className="rounded-md border-slate-200"
                      data-testid="employer-company-input"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="firstName" className="text-sm">First Name</Label>
                    <Input 
                      id="firstName"
                      name="firstName"
                      placeholder="First name" 
                      className="rounded-md border-slate-200"
                      data-testid="employer-firstname-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="lastName" className="text-sm">Last Name</Label>
                    <Input 
                      id="lastName"
                      name="lastName"
                      placeholder="Last name" 
                      className="rounded-md border-slate-200"
                      data-testid="employer-lastname-input"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="email" className="text-sm">Email</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email" 
                      placeholder="you@company.com" 
                      className="rounded-md border-slate-200"
                      data-testid="employer-email-input"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phone" className="text-sm">Phone (Optional)</Label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel" 
                    placeholder="(555) 000-0000" 
                    className="rounded-md border-slate-200"
                    data-testid="employer-phone-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="message" className="text-sm">Positions You're Hiring For</Label>
                  <Textarea 
                    id="message"
                    name="message"
                    placeholder="Tell us about the roles you're looking to fill..."
                    rows={3}
                    className="rounded-md border-slate-200 resize-none"
                    data-testid="employer-positions-input"
                    required
                  />
                </div>

                {submitStatus && (
                  <div className={`p-3 rounded-lg text-sm ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-5 text-sm disabled:opacity-50"
                  data-testid="employer-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Request Info'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
